import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AtlasRequest } from 'src/app/models/atlas-request.model';
import { AtlasResponse } from 'src/app/models/atlas-response.model';
import { CargoPaymentInfo } from 'src/app/models/cargo-payment-info.model';
import { AtlasErrorCodes, DuplicateTransactionError } from 'src/app/models/enums/atlas-error-codes.enum';
import { CustomMessageType } from 'src/app/models/enums/custom-message-type.enum';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { AddInvoiceResponse } from 'src/app/models/manual-payment/add-invoice-response.model';
import { Invoice } from 'src/app/models/manual-payment/invoice.model';
import { ModalService } from 'src/app/services/modal-service/modal.service';
import { PaymentService } from 'src/app/services/paymentService/payment.service';
import { SharedService } from 'src/app/services/shared-service/shared.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-bluefin',
  templateUrl: './bluefin.component.html',
  styleUrls: ['./bluefin.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BluefinComponent implements OnInit {
  @Input() cargoPaymentInfo: CargoPaymentInfo | undefined;
  templateRendered: boolean | undefined;
  errorMessage: string | undefined;
  token: string | undefined;
  transactionResponse!: AtlasResponse;
  sectionElement!: HTMLElement | null;
  transactionId = '';
  paymentType = '';
  baseUrl = environment.bluefinConfig.baseUrl;
  templateId = environment.bluefinConfig.templateId;
  isManualPaymentContinueDisabled = true;
  manualPaymentAttempts = environment.manualPaymentAttempts;
  isBluefinTokenRetry = false;

  constructor(public _paymentService: PaymentService, public sharedService: SharedService, public modalService: ModalService) {}

  ngOnInit(): void {
    this.sectionElement = document.getElementById('paymentSection');
    this.sharedService.currentPaymentType.subscribe((paymentType) => (this.paymentType = paymentType));
    this.sharedService.currentTransactionId.subscribe((transactionId) => (this.transactionId = transactionId));
    this.sharedService.currentIsBluefinTokenRetry.subscribe((isBluefinTokenRetry) => (this.isBluefinTokenRetry = isBluefinTokenRetry));
  }

  makeRequestToAtlas(token: string): void {
    this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoPaymentProgress));
    this.sharedService.updateLoadingState(true);
    this.sharedService.updateNextButtonName('Next');

    const amount = Number(this.cargoPaymentInfo?.paymentAdviceAmount ?? 0);
    const orderId = this.cargoPaymentInfo?.paymentAdviceNumber ?? '';
    const currency = String(this.cargoPaymentInfo?.paymentAdviceCurrency ?? 'USD');
    const awb = this.cargoPaymentInfo?.awbNumber?.split('-');
    const airlineCode = awb?.[0].trim() || '';
    const documentNumber = awb?.[1].trim() || '';
    const airportCode = this.cargoPaymentInfo?.airportCode || '';
    const passengerName = this.cargoPaymentInfo?.customerName || '';

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const payload = new AtlasRequest(token, amount, currency, orderId, airlineCode, documentNumber, airportCode, passengerName);
    this._paymentService.sendBluefin(payload).subscribe({
      next: (response: AtlasResponse) => {
        console.log(response);
        this.sharedService.updateLoadingState(false);
        this.transactionResponse = response;
        this.showPaymentSectionResult();
        this.showErrorPaymentSectionResult();
        this.sharedService.updateIsHiddenCreditCard(false);
        this.sharedService.updateNextDisabled(false);
      },
      error: (errorResponse) => {
        console.log(errorResponse);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const response = errorResponse.error as AtlasResponse;
        this.transactionResponse = response;

        if (response.errorCode != undefined && this.sharedService.existErrorCode(response.errorCode)) {
          this.showErrorPaymentSectionResult();
        } else {
          this.showErrorSection();
        }
      },
    });
  }

  closeModal(): void {
    this.modalService.close();
  }

  showLoadingPaymentSection(): void {
    if (this.paymentType == 'Manual Payment') {
      this.sharedService.updateContinueClick(true);
    }
    this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoPaymentProgress));
    this.sharedService.updateExitICargoDisabled(true);
    this.sharedService.updateLoadingState(true);
    this.sharedService.updateManualCardEntryButtonDisabled(true);
    this.sharedService.updateSubmitBluefinClick(true);
    this.sharedService.updateIsHiddenCreditCard(false);
    this.showPaymentSection(this.sectionElement);
  }

  showErrorSection(): void {
    if (this.paymentType == 'Manual Payment') {
      this.sharedService.updateContinueClick(false);
      this.sharedService.updateNextDisabled(true);
    }

    this.sharedService.updateExitICargoDisabled(false);
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateBluefinError(true);
    this.sharedService.updateSubmitBluefinClick(false);
    this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSomething));
  }

  showContractSection() {
    const contractSection = document.getElementById('contractSection');
    if (contractSection != null) {
      contractSection.hidden = true;
    }
  }

  showPaymentSectionResult() {
    if (this.validateSignaturePaymentResult() && !this.validateSignatureObject1()) {
      this.sharedService.updateErrorCreditCard(false);
      this.sharedService.updatePaymentMessage('Credit Card');
      this.sharedService.updateSuccessfulPayment(true);
      const authCode = this.transactionResponse.authorizationCode?.toString() ?? '';
      mapMessage.set(CustomMessage.SuccessPayment, {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        text: 'Payment successfully completed. <b>Auth code  - ' + authCode + ' </b>',
        type: CustomMessageType.Success,
      });

      const mapMessageToShow =
        this.transactionResponse.errorCode === DuplicateTransactionError.DuplicateOrder
          ? CustomMessage.InfoPaymentCollected
          : CustomMessage.SuccessPayment;

      this.sharedService.showMessage(mapMessage.get(mapMessageToShow));
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.sharedService.updateAuthCode(authCode);
      this.sharedService.updateSubmitBluefinClick(false);
      this.sharedService.updateManualCardEntryButtonDisabled(true);
      this.sharedService.updateNextButtonName('Done');
      this.sharedService.updateExitICargoDisabled(true);
    }
  }

  validateSignatureObject1(): boolean {
    return this.transactionResponse != undefined &&
      (this.cargoPaymentInfo?.signatureRequirement == 'AWB' ||
        this.cargoPaymentInfo?.signatureRequirement == 'AWBSecurity' ||
        this.cargoPaymentInfo?.signatureRequirement == 'Security' ||
        this.cargoPaymentInfo?.signatureRequirement == 'Delivery')
      ? true
      : false;
  }

  showErrorPaymentSectionResult() {
    if (this.validateSignaturePaymentErrorResult() && !this.validateSignatureObject1()) {
      if (this.paymentType == 'Manual Payment') {
        this.sharedService.updateNextButtonName('Done');
        this.sharedService.updateNextDisabled(true);
      }
      if (this.transactionResponse.errorCode === AtlasErrorCodes.CreditAccountError) {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorCreditAccount));
        this.sharedService.updateErrorPaymentMessage('Issue with billing account - Limit exceeded, blocked etc.');
      } else {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorDeclined));
        this.sharedService.updateErrorPaymentMessage('Transaction declined. Please exit to iCargo to try another form of payment.');
      }
      this.sharedService.updateLoadingState(false);
      this.sharedService.updateErrorCreditCard(true);
      this.sharedService.updateManualCardEntryButtonDisabled(true);
      this.sharedService.updateSubmitBluefinClick(false);
      this.sharedService.updateExitICargoDisabled(false);
      this.sharedService.updateBluefinError(false);
      this.sharedService.updateMobyDeviceError(false);
    }
  }

  showPaymentSignatureSectionResult() {
    if (this.validateSignaturePaymentResult() && this.validateSignatureObject1()) {
      this.sharedService.updateSignatureObject1(true);
      this.sharedService.updateErrorCreditCard(false);
      this.sharedService.updateMobyDeviceError(false);
      this.sharedService.updatePaymentMessage('Credit Card');
      this.sharedService.updateSuccessfulPayment(true);
      const authCode = this.transactionResponse.authorizationCode?.toString() ?? '';
      mapMessage.set(CustomMessage.SuccessPayment, {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        text: 'Signatures and payment successfully completed. <b>Auth code  - ' + authCode + ' </b>',
        type: CustomMessageType.Success,
      });
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessPayment));
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.sharedService.updateAuthCode(authCode);
      this.sharedService.updateSubmitBluefinClick(false);
      this.sharedService.updateManualCardEntryButtonDisabled(true);
    }
  }

  showErrorSignaturePaymentSectionResult() {
    if (this.validateSignaturePaymentErrorResult() && this.validateSignatureObject1()) {
      this.sharedService.updateLoadingState(false);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorDeclined));
      this.sharedService.updateErrorPaymentMessage('Transaction declined. Please exit to iCargo to try another form of payment.');
      this.sharedService.updateErrorCreditCard(true);
      this.sharedService.updateSignatureObject1(true);
      this.sharedService.updateSuccessfulPayment(false);
      this.sharedService.updateSubmitBluefinClick(false);
      this.sharedService.updateManualCardEntryButtonDisabled(true);
      this.sharedService.updateExitICargoDisabled(false);
      this.sharedService.updateBluefinError(false);
      this.sharedService.updateMobyDeviceError(false);
    }
  }

  validateSignaturePaymentResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse.isSuccess === true &&
      this.transactionResponse.authorizationCode != null
      ? true
      : false;
  }

  validateSignaturePaymentErrorResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse.isSuccess === false &&
      this.transactionResponse.authorizationCode == null
      ? true
      : false;
  }

  showPaymentSection(currentElement: HTMLElement | null) {
    if (currentElement != null && this.cargoPaymentInfo?.signatureRequired?.toLocaleLowerCase() === 'n') {
      currentElement.classList.remove('aiBoxOne');
      currentElement.classList.add('aiBoxTwo');
      currentElement.hidden = false;
    }
  }

  public copyPaymentAdviceNumber() {
    navigator.clipboard
      .writeText(this.cargoPaymentInfo?.paymentAdviceNumber || '')
      .then(() => {
        console.log('PWD number copied to clipboard');
        this.isManualPaymentContinueDisabled = false;
      })
      .catch(() => {
        console.log('Unable to copy text');
      });
  }

  close(): void {
    this.isManualPaymentContinueDisabled = true;
    this.modalService.close();
  }
  onCCManualContinue(): void {
    this.sharedService.updateIsNoSignatureNoPayment(true);
    this.modalService.close();
    this.isManualPaymentContinueDisabled = true;
    this.showContractSection();
    console.log('Manual Payment continue click');
    if (this.cargoPaymentInfo == undefined) {
      console.error('CargoPaymentInfo is undefined');
      return;
    }
    this._paymentService.sendAddManualInvoice(this.cargoPaymentInfo).subscribe({
      next: (response: AddInvoiceResponse) => {
        console.log('Manual Payment AddInvoice Response (SUCCESS): ');
        console.log(response);
        this.checkManualPaymentToken(true, this.cargoPaymentInfo?.paymentAdviceNumber || '');
      },
      error: (err) => {
        console.log('Manual Payment AddInvoice Response (ERROR): ');
        console.log(err);
        this.checkManualPaymentToken(false, this.cargoPaymentInfo?.paymentAdviceNumber || '');
      },
    });
  }

  checkManualPaymentToken(isSuccess: boolean, paymentAdviceNumber?: string): void {
    if (!isSuccess) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorManualPaymentInvoiceCreation));
      return;
    }
    this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoManualPaymentGettingPaymentData));

    this.sharedService.updateLoadingState(true);
    let count = this.manualPaymentAttempts;
    const myInterval = setInterval(() => {
      console.log(`this will be printed every 5 secs, Attempt ${count}`);
      this._paymentService.getManualInvoiceToken(paymentAdviceNumber || '').subscribe({
        next: (invoice: Invoice) => {
          console.log('Manual Payment GetInvoice Response (SUCCESS): ');
          console.log(invoice);
          if (invoice.bluefinToken != null && invoice.bluefinToken != undefined) {
            console.log('We got the bluefin token, so cleaning interval');
            clearInterval(myInterval);
            this.sharedService.updateSubmitBluefinClick(true);
            this.makeRequestToAtlas(invoice.bluefinToken);
          }
        },
        error: (err) => {
          console.log('Manual Payment GetInvoice Response (ERROR): So continue with loop');
          console.log(err);
        },
      });
      count -= 1;
      if (count == 0) {
        clearInterval(myInterval);
        console.log(`Max Retries reached Cleaning interval, Attempt ${count}`);
        this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorManualPaymentGetToken));
        this.sharedService.updateIsBluefinTokenRetry(true);
        this.modalService.open();
      }
    }, 5000); //5 secs;
  }

  onRetryToken(): void {
    console.log('onRetryToken');
    this.sharedService.updateIsNoSignatureNoPayment(true);
    this.modalService.close();
    this.isManualPaymentContinueDisabled = true;
    this.showContractSection();
    this.modalService.close();
    this._paymentService.getManualInvoiceToken(this.cargoPaymentInfo?.paymentAdviceNumber || '').subscribe({
      next: (invoice: Invoice) => {
        console.log('onRetryToken Response (SUCCESS): ');
        console.log(invoice);
        if (invoice.bluefinToken != null && invoice.bluefinToken != undefined) {
          console.log('We got the bluefin token, making request to Atlas');
          this.sharedService.updateSubmitBluefinClick(true);
          this.makeRequestToAtlas(invoice.bluefinToken);
        } else {
          this.modalService.open();
        }
      },
      error: (err) => {
        console.log('Manual Payment GetInvoice Response (ERROR)');
        console.log(err);
      },
    });
  }
}
